/**
 * EventBundler - Auto-bundles property updates within a time window.
 *
 * Handles automatic bundling of rapid property updates into single events
 * to reduce entity clutter and improve performance.
 */

// Maximum number of properties that can be bundled together
const MAX_PROPERTIES_PER_BUNDLE = 50;

// Time window for auto-bundling (milliseconds)
const BUNDLE_TIME_WINDOW_MS = 100;

// Type for entity key (entity_id + entity_type)
type EntityKey = string;

// Type for bundled property updates
interface PendingUpdate {
  entityId: string;
  entityTypeName: string;
  properties: Record<string, unknown>;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * EventBundler class for automatic property update bundling.
 */
export class EventBundler {
  private pendingUpdates: Map<EntityKey, PendingUpdate> = new Map();

  /**
   * Create a entity key from entity ID and type.
   */
  private getEntityKey(entityId: string, entityTypeName: string): EntityKey {
    return `${entityId}::${entityTypeName}`;
  }

  /**
   * Add a property update to the pending bundle.
   *
   * If no pending update exists for this entity, creates one and starts timer.
   * If pending update exists, adds property and resets timer.
   *
   * @param entityId - Entity identifier
   * @param entityTypeName - Entity type name
   * @param propertyTypeName - Property name
   * @param propertyValue - Property value
   * @param onFlush - Callback to execute when bundle is flushed
   */
  addPropertyUpdate(
    entityId: string,
    entityTypeName: string,
    propertyTypeName: string,
    propertyValue: unknown,
    onFlush: (properties: Record<string, unknown>) => void
  ): void {
    const key = this.getEntityKey(entityId, entityTypeName);
    const pending = this.pendingUpdates.get(key);

    if (pending) {
      // Clear existing timer
      if (pending.timer) {
        clearTimeout(pending.timer);
      }

      // Add property to existing bundle
      pending.properties[propertyTypeName] = propertyValue;

      const propertyCount = Object.keys(pending.properties).length;

      // Check if we've hit the max bundle size
      if (propertyCount >= MAX_PROPERTIES_PER_BUNDLE) {
        // Flush immediately if we hit the limit
        this.flushPending(key, onFlush);
        return;
      }

      // Reset timer for auto-flush
      pending.timer = setTimeout(() => {
        this.flushPending(key, onFlush);
      }, BUNDLE_TIME_WINDOW_MS);
    } else {
      // Create new pending update
      const properties = { [propertyTypeName]: propertyValue };
      const timer = setTimeout(() => {
        this.flushPending(key, onFlush);
      }, BUNDLE_TIME_WINDOW_MS);

      this.pendingUpdates.set(key, {
        entityId,
        entityTypeName,
        properties,
        timer,
      });
    }
  }

  /**
   * Flush pending updates for a specific entity.
   *
   * @param key - Entity key
   * @param onFlush - Callback to execute with bundled properties
   */
  private flushPending(
    key: EntityKey,
    onFlush: (properties: Record<string, unknown>) => void
  ): void {
    const pending = this.pendingUpdates.get(key);
    if (!pending) {
      return;
    }

    // Clear timer
    if (pending.timer) {
      clearTimeout(pending.timer);
    }

    // Execute flush callback with bundled properties
    onFlush(pending.properties);

    // Remove from pending
    this.pendingUpdates.delete(key);
  }

  /**
   * Manually flush all pending updates for a entity.
   *
   * @param entityId - Entity identifier
   * @param entityTypeName - Entity type name
   * @param onFlush - Callback to execute with bundled properties
   */
  flush(
    entityId: string,
    entityTypeName: string,
    onFlush: (properties: Record<string, unknown>) => void
  ): void {
    const key = this.getEntityKey(entityId, entityTypeName);
    this.flushPending(key, onFlush);
  }

  /**
   * Flush all pending updates across all entities.
   *
   * @param onFlush - Callback to execute for each entity's bundled properties
   */
  flushAll(
    onFlush: (
      entityId: string,
      entityTypeName: string,
      properties: Record<string, unknown>
    ) => void
  ): void {
    for (const [_key, pending] of this.pendingUpdates.entries()) {
      // Clear timer
      if (pending.timer) {
        clearTimeout(pending.timer);
      }

      // Execute flush callback
      onFlush(pending.entityId, pending.entityTypeName, pending.properties);
    }

    // Clear all pending
    this.pendingUpdates.clear();
  }

  /**
   * Check if there are pending updates for a entity.
   */
  hasPending(entityId: string, entityTypeName: string): boolean {
    const key = this.getEntityKey(entityId, entityTypeName);
    return this.pendingUpdates.has(key);
  }

  /**
   * Get pending property count for a entity.
   */
  getPendingCount(entityId: string, entityTypeName: string): number {
    const key = this.getEntityKey(entityId, entityTypeName);
    const pending = this.pendingUpdates.get(key);
    return pending ? Object.keys(pending.properties).length : 0;
  }

  /**
   * Clear all pending updates (useful for cleanup).
   */
  clear(): void {
    // Clear all timers
    for (const pending of this.pendingUpdates.values()) {
      if (pending.timer) {
        clearTimeout(pending.timer);
      }
    }

    // Clear pending map
    this.pendingUpdates.clear();
  }
}

/**
 * Factory function for creating event bundlers.
 */
export function createEventBundler(): EventBundler {
  return new EventBundler();
}
